/**
 * Camera access + paper-photo processing helpers for CaptureFlow.
 *
 * Sources, in order: getUserMedia (rear camera) → Capacitor Camera on native → <input type=file>.
 * Processing: decode → 4-corner straighten (core warpQuad) → autoLevels → contrast(1.1) → JPEG
 * with the long side ≤ 1200 px.
 */
import { Capacitor } from '@capacitor/core';
import { autoLevels, contrast, quadOutputSize, warpQuad, type Img, type Quad } from '@sketchling/core';
import { canvasToBlob, loadImage } from './journal';

export const OUTPUT_LONG_SIDE = 1200;
/** Working resolution for the crop step (keeps warp fast on phones). */
export const WORK_LONG_SIDE = 1600;

export type CameraError = 'denied' | 'unavailable';

export const hasMediaDevices = () => typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
export const isNativeCamera = () => Capacitor.isNativePlatform();

/** Open the rear camera (landscape-friendly resolution). Throws a CameraError string. */
export async function openRearCamera(): Promise<MediaStream> {
  if (!hasMediaDevices()) throw 'unavailable' as CameraError;
  try {
    return await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
    });
  } catch (e) {
    const name = (e as { name?: string })?.name ?? '';
    throw (name === 'NotAllowedError' || name === 'SecurityError' ? 'denied' : 'unavailable') as CameraError;
  }
}

export function stopStream(s: MediaStream | null | undefined): void {
  s?.getTracks().forEach((t) => t.stop());
}

/** Current video frame as a canvas (long side ≤ WORK_LONG_SIDE). */
export function grabFrame(video: HTMLVideoElement): HTMLCanvasElement | null {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) return null;
  const s = Math.min(1, WORK_LONG_SIDE / Math.max(vw, vh));
  const c = document.createElement('canvas');
  c.width = Math.round(vw * s);
  c.height = Math.round(vh * s);
  c.getContext('2d')!.drawImage(video, 0, 0, c.width, c.height);
  return c;
}

/** Native camera via @capacitor/camera (null when cancelled). */
export async function nativePhoto(source: 'camera' | 'gallery' = 'camera'): Promise<Blob | null> {
  try {
    const { Camera, CameraResultType, CameraSource } = await import('@capacitor/camera');
    const photo = await Camera.getPhoto({
      resultType: CameraResultType.Uri,
      source: source === 'camera' ? CameraSource.Camera : CameraSource.Photos,
      quality: 85,
      width: WORK_LONG_SIDE,
      correctOrientation: true,
      saveToGallery: false,
    });
    if (!photo.webPath) return null;
    return await (await fetch(photo.webPath)).blob();
  } catch {
    return null;
  }
}

/** Decode a blob into pixels (long side ≤ maxSide). */
export async function blobToImg(blob: Blob, maxSide = WORK_LONG_SIDE): Promise<{ img: Img; canvas: HTMLCanvasElement }> {
  const url = URL.createObjectURL(blob);
  try {
    const el = await loadImage(url);
    const s = Math.min(1, maxSide / Math.max(el.width, el.height));
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(el.width * s));
    c.height = Math.max(1, Math.round(el.height * s));
    const ctx = c.getContext('2d', { willReadFrequently: true })!;
    ctx.drawImage(el, 0, 0, c.width, c.height);
    const d = ctx.getImageData(0, 0, c.width, c.height);
    return { img: { width: d.width, height: d.height, data: d.data }, canvas: c };
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function canvasToImg(c: HTMLCanvasElement): Img {
  const d = c.getContext('2d', { willReadFrequently: true })!.getImageData(0, 0, c.width, c.height);
  return { width: d.width, height: d.height, data: d.data };
}

export async function imgToBlob(img: Img, type = 'image/jpeg', quality = 0.86): Promise<Blob> {
  const c = document.createElement('canvas');
  c.width = img.width;
  c.height = img.height;
  c.getContext('2d')!.putImageData(new ImageData(img.data, img.width, img.height), 0, 0);
  return canvasToBlob(c, type, quality);
}

/** Straighten + clean up a paper photo. */
export async function processPaperPhoto(src: Img, quad: Quad): Promise<Blob> {
  const { w, h } = quadOutputSize(quad, Math.min(OUTPUT_LONG_SIDE, Math.max(src.width, src.height)));
  let out = warpQuad(src, quad, w, h);
  out = autoLevels(out);
  out = contrast(out, 1.1);
  return imgToBlob(out);
}

/** Let the user pick (or shoot, on mobile web) a photo with a hidden file input. */
export function pickFile(capture: boolean): Promise<Blob | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    if (capture) input.setAttribute('capture', 'environment');
    input.style.display = 'none';
    let settled = false;
    const finish = (b: Blob | null) => {
      if (settled) return;
      settled = true;
      input.remove();
      resolve(b);
    };
    input.onchange = () => finish(input.files?.[0] ?? null);
    input.addEventListener('cancel', () => finish(null));
    document.body.appendChild(input);
    input.click();
  });
}
